import { useMemo, useState } from "react";
import type { BundleIndex, FeedSummary, FeedItem } from "../lib/types";
import {
  classificationsByUrl,
  feedHealth,
  feedLabel,
  normalizeUrl,
  runIdByPostUrl,
  windowSummary,
} from "../lib/feeds";
import type { Classification } from "../lib/feeds";

type Props = {
  index: BundleIndex;
  // Jump the run column below to the run that processed a given blog post.
  onSelectRun: (runId: string) => void;
};

// Day-/window-level RSS panel. Sits under the Summary header so it's visible
// regardless of which run is selected. Its job: answer the two questions that
// matter at a glance — how many NEW blog posts landed in this window, and how
// many packages were processed — then confirm each feed was actually READ &
// PARSED even on the common "0 new" day. A run-list view can't show this,
// because a 0-new fetch produces no packages and no classifier events.
export function Feeds({ index, onSelectRun }: Props) {
  const feeds = index.feeds ?? [];
  const summary = windowSummary(index);
  // What WE classified each post as (from the Anthropic classify logs), joined
  // to feed items by URL. Recomputed only when the bundle changes.
  const classifications = useMemo(() => classificationsByUrl(index), [index]);
  // Which run processed each post, so a blog entry can jump to its run below.
  const runIdByUrl = useMemo(() => runIdByPostUrl(index), [index]);

  return (
    <section className="feeds">
      <div className="feeds__header">
        <span className="feeds__title">RSS feeds</span>
        <span className="feeds__window" title={summary.windowRange ?? undefined}>
          {summary.windowLabel}
        </span>
      </div>

      <div className="feeds__stats" role="group" aria-label="Window summary">
        <Stat
          value={summary.newPosts}
          label={`new post${summary.newPosts === 1 ? "" : "s"}`}
          tone={summary.newPosts > 0 ? "good" : "muted"}
        />
        <Stat value={summary.packagesProcessed} label="processed" tone="neutral" />
        <Stat
          value={summary.needAction}
          label="need action"
          tone={summary.needAction > 0 ? "bad" : "muted"}
          title={`${summary.actionBreakdown.block} block · ${summary.actionBreakdown.review} review`}
        />
      </div>

      {feeds.length === 0 ? (
        <p className="feeds__muted">
          No feed records in this bundle. Older bundle, or the lambda isn't writing the
          <code> feed/</code> prefix yet (check the IAM grant + redeploy).
        </p>
      ) : (
        <ul className="feeds__list">
          {feeds.map((feed) => (
            <FeedRow
              key={feed.sourceId}
              feed={feed}
              classifications={classifications}
              runIdByUrl={runIdByUrl}
              onSelectRun={onSelectRun}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function Stat({
  value,
  label,
  tone,
  title,
}: {
  value: number;
  label: string;
  tone: "good" | "bad" | "muted" | "neutral";
  title?: string;
}) {
  return (
    <div className={`feeds__stat feeds__stat--${tone}`} title={title}>
      <span className="feeds__stat-value">{value}</span>
      <span className="feeds__stat-label">{label}</span>
    </div>
  );
}

function FeedRow({
  feed,
  classifications,
  runIdByUrl,
  onSelectRun,
}: {
  feed: FeedSummary;
  classifications: Map<string, Classification>;
  runIdByUrl: Map<string, string>;
  onSelectRun: (runId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const health = feedHealth(feed);
  const items = feed.latest?.items ?? [];
  const canExpand = items.length > 0;
  // The narrowest ingest filter the feed applies (category, keyword or age),
  // or null when it filters on nothing. Separate from OUR classification
  // below: this only describes the parsed→reached-the-classifier funnel.
  const filterStage = health.filterStage;
  const lastFetch = feed.latest?.timestamp
    ? new Date(feed.latest.timestamp).toLocaleString()
    : null;

  return (
    <li className={`feed feed--${health.tone}`}>
      <button
        className="feed__head"
        onClick={() => canExpand && setOpen((v) => !v)}
        aria-expanded={open}
        disabled={!canExpand}
        title={canExpand ? "Show the URLs parsed from this feed" : undefined}
      >
        <span className={`feed__dot feed__dot--${health.tone}`} aria-hidden />
        <span className="feed__name">{feedLabel(feed)}</span>

        {/* Lead with the new-post count — the headline signal. Calm when 0
            (the common, healthy case), highlighted when >0. */}
        <span className={`feed__new feed__new--${health.newPosts > 0 ? "good" : "muted"}`}>
          {health.newPosts} new post{health.newPosts === 1 ? "" : "s"}
        </span>

        {health.state === "ok" ? (
          <span className="feed__pipeline">
            {filterStage
              ? `${health.parsed} parsed · ${filterStage.count} ${filterStage.label}`
              : `${health.parsed} parsed · no filter`}
          </span>
        ) : (
          <span className="feed__headline">{health.headline}</span>
        )}

        <span className="feed__meta">
          {feed.fetchCount} fetch{feed.fetchCount === 1 ? "" : "es"}
          {lastFetch ? ` · last ${lastFetch}` : ""}
        </span>
        {canExpand && <span className="feed__chevron">{open ? "▾" : "▸"}</span>}
      </button>
      {open && canExpand && (
        <p className="feed__note">
          “Our category” is what our Anthropic classify step decided, read from
          its logs — not the RSS feed’s own tags. “—” means the post wasn’t
          classified in this window (only new posts are classified).
        </p>
      )}
      {open && canExpand && (
        <table className="feed__items">
          <thead>
            <tr>
              <th>Post</th>
              <th>Published</th>
              <th title="Our Anthropic classification, from the classify-step logs">
                Our category
              </th>
              <th>State</th>
              <th title="Jump to the run that processed this post">Run</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <FeedItemRow
                key={item.url ?? `${item.title}-${i}`}
                item={item}
                classification={item.url ? classifications.get(normalizeUrl(item.url)) : undefined}
                runId={item.url ? runIdByUrl.get(normalizeUrl(item.url)) : undefined}
                onSelectRun={onSelectRun}
              />
            ))}
          </tbody>
        </table>
      )}
    </li>
  );
}

function FeedItemRow({
  item,
  classification,
  runId,
  onSelectRun,
}: {
  item: FeedItem;
  classification: Classification | undefined;
  runId: string | undefined;
  onSelectRun: (runId: string) => void;
}) {
  const stateBadge = !item.passedCategoryFilter
    ? { label: "off-topic", cls: "muted" }
    : item.isNew
      ? { label: "new", cls: "good" }
      : { label: "seen", cls: "muted" };
  return (
    <tr className={item.passedCategoryFilter ? "" : "feed__item--filtered"}>
      <td className="feed__item-title">
        {item.url ? (
          <a href={item.url} target="_blank" rel="noreferrer">
            {item.title || item.url}
          </a>
        ) : (
          item.title || "(untitled)"
        )}
      </td>
      <td className="feed__item-date">{item.publishedAt?.slice(0, 10) ?? "—"}</td>
      <td className="feed__item-cat" title={classification?.rationale ?? undefined}>
        {classification ? classification.category : "—"}
      </td>
      <td>
        <span className={`feed__badge feed__badge--${stateBadge.cls}`}>{stateBadge.label}</span>
      </td>
      <td className="feed__item-run">
        {runId ? (
          <button
            type="button"
            className="feed__run-link"
            onClick={() => onSelectRun(runId)}
            title={`Select run ${runId.slice(0, 8)} below`}
          >
            view run {runId.slice(0, 8)} →
          </button>
        ) : (
          <span className="feed__run-none" title="This post was not processed in a run in this window">
            —
          </span>
        )}
      </td>
    </tr>
  );
}
